import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import NavBar from "@/components/NavBar";
import Footer from "@/components/Footer";
import ValueScoreBadge from "@/components/ValueScoreBadge";
import QRClaimModal from "@/components/QRClaimModal";
import { products as pApi, reviews as rApi, deals as dealsApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Star, Award, Sparkles, MapPin, Send, Flame, ThumbsUp, ThumbsDown } from "lucide-react";
import { toast, Toaster } from "sonner";

const FLAVOR_OPTIONS = ["citrus", "pine", "earthy", "sweet", "berry", "diesel", "spice", "vanilla", "mint", "mango", "sour"];

export default function ProductDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [revs, setRevs] = useState([]);
  const [activeDeal, setActiveDeal] = useState(null);
  const [showQR, setShowQR] = useState(false);
  const [reviewForm, setReviewForm] = useState({ rating: 5, body: "", flavor_tags: [], verdict: "GAS" });
  const { user } = useAuth();

  useEffect(() => {
    pApi.get(id).then(setP);
    rApi.list(id).then(setRevs);
  }, [id]);

  // load matching deal for this product (from dispensary)
  useEffect(() => {
    if (!p) return;
    dealsApi.list().then((all) => {
      const match = all.find((d) => d.dispensary_id === p.dispensary_id);
      if (match) setActiveDeal(match);
    });
  }, [p]);

  const submitReview = async () => {
    if (!user) { toast.error("Sign in to drop a review"); return; }
    if (!reviewForm.body.trim()) { toast.error("Add a body"); return; }
    try {
      const tags = [...reviewForm.flavor_tags, reviewForm.verdict];
      await rApi.create({ product_id: id, rating: reviewForm.rating, body: reviewForm.body, flavor_tags: tags });
      const fresh = await rApi.list(id);
      setRevs(fresh);
      setReviewForm({ rating: 5, body: "", flavor_tags: [], verdict: "GAS" });
      const fp = await pApi.get(id);
      setP(fp);
      toast.success("Review posted · +5 XP 🌿");
    } catch (e) { toast.error("Could not post review"); }
  };

  if (!p) return <div className="min-h-screen"><NavBar /><div className="text-center py-32">Loading...</div></div>;

  // GAS / PASS ratio
  const gasCount = revs.filter((r) => (r.flavor_tags || []).includes("GAS")).length;
  const passCount = revs.filter((r) => (r.flavor_tags || []).includes("PASS")).length;
  const total = gasCount + passCount;
  const gasRatio = total ? Math.round((gasCount / total) * 100) : 78;

  const strainTone = { indica: "card-purple", sativa: "card-sunset", hybrid: "card-green", cbd: "card-cyan" }[p.strain] || "card-green";

  return (
    <div className="min-h-screen relative overflow-hidden">
      <div className="blob" style={{ width: 380, height: 380, background: "#15FF73", top: -100, left: -80 }} />
      <NavBar />
      <Toaster position="top-right" theme="light" richColors />
      <div className="max-w-7xl mx-auto px-5 lg:px-10 py-10 relative">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-6">
            <div className={`card-x ${strainTone} bg-[#FAF6E8] overflow-hidden`}>
              <div className="aspect-square overflow-hidden bg-[#0B0F0C]"><img src={p.image_url} alt={p.name} className="w-full h-full object-cover" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3 mt-4">
              <Spec label="THC" value={`${p.thc.toFixed(0)}%`} accent />
              <Spec label="CBD" value={`${p.cbd.toFixed(0)}%`} />
              <Spec label="Weight" value={`${p.weight_g}g`} />
            </div>
          </div>
          <div className="lg:col-span-6">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <span className={`sticker ${({ indica: "sticker-purple", sativa: "sticker-sunset", hybrid: "sticker-green", cbd: "sticker-cyan" })[p.strain]} !text-sm`}>{p.strain}</span>
              {p.is_top_shelf && <span className="sticker sticker-lemon !text-sm" style={{ transform: "rotate(2deg)" }}><Award size={11} strokeWidth={2.4} /> Top Shelf</span>}
              {p.is_sleeper_pick && <span className="sticker sticker-magenta !text-sm" style={{ transform: "rotate(-3deg)" }}><Sparkles size={11} strokeWidth={2.4} /> Sleeper</span>}
            </div>
            <h1 className="font-display text-5xl sm:text-6xl font-extrabold leading-[0.92] tracking-tight mb-2">{p.name}</h1>
            <div className="text-sm text-[#0B0F0C]/55 mb-5">{p.brand}</div>

            <div className="flex items-center gap-4 mb-7 flex-wrap">
              <ValueScoreBadge score={p.value_score} size="lg" />
              <div className="flex items-center gap-1 text-sm">
                <Star size={14} className="fill-[#FF7A3D] text-[#FF7A3D]" />
                <span className="font-mono-x font-bold">{p.rating.toFixed(1)}</span>
                <span className="text-[#0B0F0C]/40 mx-1">·</span>
                <span className="text-[#0B0F0C]/60">{p.review_count} reviews</span>
              </div>
            </div>

            {/* GAS PASS RATIO */}
            <div className="card-x card-green bg-[#FAF6E8] p-5 mb-6">
              <div className="flex items-center justify-between mb-3">
                <span className="label-overline text-[#FF1A8C]">Smoke Test</span>
                <span className="font-mono-x text-sm font-bold">{total || revs.length} reviewers</span>
              </div>
              <div className="flex h-9 rounded-full overflow-hidden border-2 border-[#0B0F0C]">
                <div className="bg-[#15FF73] flex items-center justify-center text-xs font-bold text-[#0B0F0C]" style={{ width: `${gasRatio}%` }}>
                  {gasRatio >= 15 && <span className="px-2">🔥 {gasRatio}% GAS</span>}
                </div>
                <div className="bg-[#FF1A8C] flex items-center justify-center text-xs font-bold text-white" style={{ width: `${100 - gasRatio}%` }}>
                  {100 - gasRatio >= 15 && <span className="px-2">{100 - gasRatio}% PASS</span>}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3 mb-6">
              <PriceCell label="Price" value={`$${p.price.toFixed(0)}`} accent />
              <PriceCell label="Per Gram" value={`$${p.price_per_gram.toFixed(2)}`} />
              <PriceCell label="$/THC%" value={`$${(p.price / Math.max(p.thc, 1)).toFixed(2)}`} />
            </div>

            <div className="mb-5">
              <div className="label-overline text-[#FF1A8C] mb-2">Effects</div>
              <div className="flex flex-wrap gap-1.5">
                {p.effects.map((e) => <span key={e} className="sticker sticker-cyan !text-[10px]" style={{ transform: `rotate(${Math.random()*4-2}deg)` }}>{e}</span>)}
              </div>
            </div>
            <div className="mb-7">
              <div className="label-overline text-[#FF1A8C] mb-2">Flavor profile</div>
              <div className="flex flex-wrap gap-1.5">
                {p.flavors.map((f) => <span key={f} className="sticker sticker-lemon !text-[10px]" style={{ transform: `rotate(${Math.random()*4-2}deg)` }}>{f}</span>)}
              </div>
            </div>

            {p.dispensary && (
              <div className="card-x card-magenta bg-[#FAF6E8] p-5 flex items-center justify-between">
                <div>
                  <div className="label-overline text-[#FF1A8C] flex items-center gap-1.5"><MapPin size={11} strokeWidth={2.4} /> Sold at</div>
                  <div className="font-display text-2xl font-extrabold mt-1">{p.dispensary.name}</div>
                  <div className="text-xs text-[#0B0F0C]/55 font-mono-x mt-0.5">{p.dispensary.city}, {p.dispensary.state} · {p.dispensary.type}</div>
                </div>
                {activeDeal ? (
                  <button onClick={() => setShowQR(true)} className="btn-x btn-primary"><Flame size={14} strokeWidth={2.4} /> Claim deal</button>
                ) : (
                  <Link to={`/dispensary/${p.dispensary.id}`} className="btn-x btn-magenta">Visit shop →</Link>
                )}
              </div>
            )}
          </div>
        </div>

        {/* REVIEWS */}
        <section className="mt-16">
          <div className="flex items-end justify-between mb-7 gap-4">
            <div>
              <div className="label-overline text-[#FF1A8C] mb-2">Smoke tests</div>
              <h2 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight">Word on the street.</h2>
            </div>
            <span className="font-mono-x text-sm font-bold">{revs.length} reviews</span>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              {revs.length === 0 && <div className="card-x p-8 bg-[#FAF6E8] text-[#0B0F0C]/55 text-sm">No reviews yet — be the first to drop a smoke test 🔥</div>}
              {revs.map((r) => {
                const isGas = (r.flavor_tags || []).includes("GAS");
                const isPass = (r.flavor_tags || []).includes("PASS");
                const verdict = isGas ? "GAS" : isPass ? "PASS" : null;
                return (
                  <div key={r.id} className={`card-x ${isGas ? "card-green" : isPass ? "card-magenta" : "card-cyan"} bg-[#FAF6E8] p-5`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-9 h-9 rounded-full border-2 border-[#0B0F0C] bg-[#FFE74C] flex items-center justify-center font-display font-bold">{(r.user_name || "?")[0]}</div>
                        <div>
                          <div className="font-bold text-sm">{r.user_name || "Anon"}</div>
                          <div className="flex items-center gap-0.5">{[...Array(5)].map((_, i) => <Star key={i} size={11} strokeWidth={2.4} className={i < r.rating ? "fill-[#FF7A3D] text-[#FF7A3D]" : "text-[#0B0F0C]/20"} />)}</div>
                        </div>
                      </div>
                      {verdict && <span className={`sticker ${verdict === "GAS" ? "sticker-green" : "sticker-magenta"} !text-sm`} style={{ transform: "rotate(-3deg)" }}>{verdict === "GAS" ? "🔥 GAS" : "👎 PASS"}</span>}
                    </div>
                    <p className="text-sm text-[#0B0F0C] leading-relaxed mb-3 font-medium">"{r.body}"</p>
                    {(r.flavor_tags || []).filter((t) => !["GAS","PASS"].includes(t)).length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {(r.flavor_tags || []).filter((t) => !["GAS","PASS"].includes(t)).map((t) => <span key={t} className="text-[10px] px-2 py-0.5 rounded-full border border-[#0B0F0C] font-mono-x font-bold uppercase tracking-wider">{t}</span>)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="card-x card-sunset bg-[#FAF6E8] p-6 h-fit lg:sticky lg:top-24">
              <div className="label-overline text-[#FF1A8C] mb-3">Drop a smoke test</div>
              <div className="flex gap-2 mb-4">
                <button data-testid="verdict-gas" onClick={() => setReviewForm({ ...reviewForm, verdict: "GAS" })} className={`btn-x !py-2 !px-4 !text-xs flex-1 ${reviewForm.verdict === "GAS" ? "btn-primary" : "btn-ghost"}`}>
                  <ThumbsUp size={12} strokeWidth={2.4} /> GAS
                </button>
                <button data-testid="verdict-pass" onClick={() => setReviewForm({ ...reviewForm, verdict: "PASS" })} className={`btn-x !py-2 !px-4 !text-xs flex-1 ${reviewForm.verdict === "PASS" ? "btn-magenta" : "btn-ghost"}`}>
                  <ThumbsDown size={12} strokeWidth={2.4} /> PASS
                </button>
              </div>
              <div className="mb-4">
                <div className="text-xs text-[#0B0F0C]/55 mb-2 font-bold">Stars</div>
                <div className="flex gap-1">
                  {[1,2,3,4,5].map((n) => (
                    <button key={n} data-testid={`star-${n}`} onClick={() => setReviewForm({ ...reviewForm, rating: n })} className="p-1">
                      <Star size={22} strokeWidth={2.4} className={n <= reviewForm.rating ? "fill-[#FF7A3D] text-[#FF7A3D]" : "text-[#0B0F0C]/20"} />
                    </button>
                  ))}
                </div>
              </div>
              <textarea data-testid="review-body" value={reviewForm.body} onChange={(e) => setReviewForm({ ...reviewForm, body: e.target.value })} rows={4}
                placeholder="Hit like a freight train. Citrus on the exhale. Best at..." className="w-full bg-[#FAF6E8] border-2 border-[#0B0F0C] rounded-2xl outline-none p-3 text-sm placeholder:text-[#0B0F0C]/40 resize-none focus:bg-white focus:shadow-[3px_3px_0_#0B0F0C] transition-all" />
              <div className="mt-4">
                <div className="text-xs text-[#0B0F0C]/55 mb-2 font-bold">Flavor tags</div>
                <div className="flex flex-wrap gap-1.5">
                  {FLAVOR_OPTIONS.map((f) => {
                    const on = reviewForm.flavor_tags.includes(f);
                    return (
                      <button key={f} data-testid={`flavor-tag-${f}`} onClick={() => setReviewForm({ ...reviewForm, flavor_tags: on ? reviewForm.flavor_tags.filter((x) => x !== f) : [...reviewForm.flavor_tags, f] })}
                        className={`text-[10px] font-mono-x font-bold uppercase tracking-widest px-2 py-1 rounded-full border-2 transition-all ${on ? "bg-[#15FF73] border-[#0B0F0C] shadow-[2px_2px_0_#0B0F0C]" : "border-[#0B0F0C]/30 hover:border-[#0B0F0C]"}`}>{f}</button>
                    );
                  })}
                </div>
              </div>
              <button data-testid="submit-review" onClick={submitReview} className="btn-x btn-primary mt-5 w-full"><Send size={14} strokeWidth={2.4} /> Drop it (+5 XP)</button>
            </div>
          </div>
        </section>
      </div>
      {showQR && activeDeal && <QRClaimModal deal={activeDeal} onClose={() => setShowQR(false)} />}
      <Footer />
    </div>
  );
}

function Spec({ label, value, accent }) {
  return (
    <div className={`card-x ${accent ? "card-green" : ""} bg-[#FAF6E8] p-3 text-center`}>
      <div className="label-overline text-[#0B0F0C]/55">{label}</div>
      <div className="font-display text-2xl font-extrabold mt-1">{value}</div>
    </div>
  );
}
function PriceCell({ label, value, accent }) {
  return (
    <div className={`card-x ${accent ? "card-green" : ""} bg-[#FAF6E8] p-4`}>
      <div className="label-overline text-[#0B0F0C]/55">{label}</div>
      <div className={`font-display text-2xl font-extrabold mt-1 ${accent ? "text-[#0B0F0C]" : ""}`}>{value}</div>
    </div>
  );
}
